import React, { Suspense } from 'react';
import { getRegistryEntry } from './cmsComponentRegistry';
// eslint-disable-next-line no-duplicate-imports
import type { CmsNode } from './cmsComponentRegistry';
import DataFetchingWrapper from './components/DataFetchingWrapper';
// eslint-disable-next-line no-duplicate-imports
import type { DataFetchConfig } from './components/DataFetchingWrapper';
import Loader from '../../components/Loader/Loader';

interface CmsRendererProps {
  /**
   * The CMS node to render
   */
  node: CmsNode | null;
  /**
   * Optional fallback component when node type is not found in registry
   */
  fallback?: React.ReactNode;
  /**
   * Optional additional props to pass to the component (e.g., tabIndex)
   */
  extraProps?: Record<string, unknown>;
}

/**
 * Loading fallback for lazy-loaded components
 */
const LoadingFallback = (): React.ReactElement => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      minHeight: '100px',
    }}
  >
    <Loader />
  </div>
);

/**
 * Check if a config object is a valid data fetching config
 */
const isValidDataFetchConfig = (config: unknown): config is DataFetchConfig => {
  return (
    config !== null &&
    config !== undefined &&
    typeof config === 'object' &&
    typeof (config as Record<string, unknown>).query === 'string'
  );
};

/**
 * CmsRenderer - Recursive component that renders CMS data structure
 *
 * This component takes a CMS node and:
 * 1. Looks up the registry entry using _modelApiKey
 * 2. Transforms CMS props to component props using the transform function
 * 3. Recursively renders children based on childrenKey
 * 4. Wraps with optional Wrapper component for state/context management
 * 5. Uses Suspense for lazy-loaded components
 * 6. Wraps with DataFetchingWrapper if node has valid config for data fetching
 */
const CmsRenderer: React.FC<CmsRendererProps> = ({
  node,
  fallback = null,
  extraProps = {},
}) => {
  // Handle null/undefined node
  if (!node) {
    return <>{fallback}</>;
  }

  // Get the _modelApiKey to determine which component to render
  const modelApiKey = node._modelApiKey;

  if (!modelApiKey) {
    console.warn('[CmsRenderer] Node missing _modelApiKey:', node);
    return <>{fallback}</>;
  }

  // Look up the registry entry
  const entry = getRegistryEntry(modelApiKey);

  if (entry === null || entry === undefined) {
    console.warn(
      `[CmsRenderer] No component registered for _modelApiKey: "${modelApiKey}"`,
      node
    );
    return <>{fallback}</>;
  }

  const { Component, transform, childrenKey, Wrapper } = entry;

  // Get children from the specified key(s) (e.g., 'tabs', 'blocks', ['blocks', 'content'])
  // Supports single key or array of keys - merges children from all keys
  const getChildNodes = (): CmsNode[] => {
    if (childrenKey === null || childrenKey === undefined) {
      return [];
    }

    if (Array.isArray(childrenKey)) {
      // Merge children from multiple keys
      return childrenKey.flatMap(
        (key) => (node[key] as CmsNode[] | undefined) ?? []
      );
    }

    return (node[childrenKey] as CmsNode[] | undefined) ?? [];
  };

  const childNodes: CmsNode[] = getChildNodes();

  // Helper to render children - can receive dataProps to pass down (e.g., cmsData)
  const renderChildren = (
    dataProps?: Record<string, unknown>
  ): React.ReactNode =>
    childNodes.length > 0
      ? childNodes.map((childNode, index) => (
          <CmsRenderer
            key={childNode.id || `cms-child-${index}`}
            node={childNode}
            fallback={fallback}
            extraProps={{ ...extraProps, tabIndex: index, ...dataProps }}
          />
        ))
      : null;

  // Helper to render component with given props
  const renderComponent = (
    additionalProps: Record<string, unknown> = {}
  ): React.ReactElement => {
    const componentProps = {
      ...transform(node),
      ...extraProps,
      ...additionalProps,
    };

    const content = (
      <Suspense fallback={<LoadingFallback />}>
        <Component {...componentProps}>
          {renderChildren(additionalProps)}
        </Component>
      </Suspense>
    );

    if (Wrapper !== null && Wrapper !== undefined) {
      return (
        <Wrapper cmsNode={node} componentProps={componentProps}>
          {content}
        </Wrapper>
      );
    }

    return content;
  };

  // Check if node has data fetching config
  // If so, wrap with DataFetchingWrapper and pass data via props
  if (isValidDataFetchConfig(node.config)) {
    return (
      <DataFetchingWrapper config={node.config}>
        {(dataProps): React.ReactElement =>
          renderComponent({ cmsData: dataProps })
        }
      </DataFetchingWrapper>
    );
  }

  return renderComponent();
};

export default CmsRenderer;
